import { Controller } from "@hotwired/stimulus"
import {
  fileExtensionIsAccepted,
  fileType,
  formatBytes,
  replaceInputFiles
} from "../package_upload/files.js"
import {
  fileSelectionError,
  interpolateMessage
} from "../package_upload/validation.js"

export default class extends Controller {
  static targets = [
    "filesInput",
    "folderInput",
    "dropzone",
    "fileError",
    "pickerTitle",
    "details",
    "detailsHeading",
    "fileRows",
    "row",
    "rowTemplate",
    "fileCount",
    "totalSize",
    "validationSummary"
  ]

  static values = {
    maxFileSize: Number,
    maxTotalSize: Number,
    messages: Object
  }

  connect() {
    this.activeInput = null
    this.dragDepth = 0
    this.detailsTarget.hidden = this.rowTargets.length === 0
    this.syncPickerState()
  }

  filesChanged() {
    this.selectFrom(this.filesInputTarget, this.folderInputTarget)
  }

  folderChanged() {
    this.selectFrom(this.folderInputTarget, this.filesInputTarget)
  }

  removeFile(event) {
    const row = event.currentTarget.closest("[data-intake-upload-target='row']")
    if (!row) return

    if (this.activeInput) {
      const removedIndex = Number(row.dataset.rowIndex)
      const files = Array.from(this.activeInput.files).filter((_file, index) => index !== removedIndex)
      replaceInputFiles(this.activeInput, files)
      this.renderFiles(files)
    } else {
      row.remove()
      this.renumberRows()
      this.syncPickerState()
    }
  }

  dragEntered(event) {
    event.preventDefault()
    this.dragDepth += 1
    this.dropzoneTarget.classList.add("dragging")
  }

  dragOver(event) {
    event.preventDefault()
  }

  dragLeft(event) {
    event.preventDefault()
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.dropzoneTarget.classList.remove("dragging")
  }

  filesDropped(event) {
    event.preventDefault()
    this.dragDepth = 0
    this.dropzoneTarget.classList.remove("dragging")
    this.folderInputTarget.value = ""
    this.activeInput = this.filesInputTarget
    replaceInputFiles(this.filesInputTarget, Array.from(event.dataTransfer.files))
    this.renderFiles(Array.from(this.filesInputTarget.files), { moveFocus: true })
  }

  validateSubmission(event) {
    const files = this.selectedFiles
    const message = fileSelectionError({
      files,
      accept: this.filesInputTarget.accept,
      reviewedFileCount: this.rowTargets.length,
      maxFileSize: this.maxFileSizeValue,
      maxTotalSize: this.maxTotalSizeValue,
      messages: this.messagesValue
    })
    this.syncFileError(message)
    if (!message) return

    event.preventDefault()
    this.validationSummaryTarget.textContent = message
    this.validationSummaryTarget.hidden = false
    this.validationSummaryTarget.focus()
  }

  selectFrom(input, otherInput) {
    otherInput.value = ""
    this.activeInput = input
    this.renderFiles(Array.from(input.files), { moveFocus: true })
  }

  renderFiles(files, { moveFocus = false } = {}) {
    this.fileRowsTarget.replaceChildren()
    files.forEach((file, index) => this.fileRowsTarget.append(this.buildRow(file, index)))
    this.syncPickerState(files)
    this.syncFileError(fileSelectionError({
      files,
      accept: this.filesInputTarget.accept,
      reviewedFileCount: files.length,
      maxFileSize: this.maxFileSizeValue,
      maxTotalSize: this.maxTotalSizeValue,
      messages: this.messagesValue
    }))
    this.validationSummaryTarget.hidden = true

    if (moveFocus && files.length > 0) {
      window.requestAnimationFrame(() => this.detailsHeadingTarget.focus())
    }
  }

  buildRow(file, index) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    const sourcePath = file.webkitRelativePath || file.name
    const path = row.querySelector("[data-upload-display='filePath']")
    const error = row.querySelector("[data-upload-display='fileError']")
    let errorMessage = null

    row.dataset.rowIndex = index
    row.querySelector("[data-upload-display='fileType']").textContent = fileType(
      file.name,
      this.messagesValue.file_marker
    )
    row.querySelector("[data-upload-display='fileName']").textContent = file.name
    row.querySelector("[data-upload-display='fileSize']").textContent = formatBytes(file.size)
    path.textContent = sourcePath
    path.hidden = !sourcePath.includes("/")

    if (!fileExtensionIsAccepted(file.name, this.filesInputTarget.accept)) {
      errorMessage = this.messagesValue.unsupported_file
    } else if (file.size === 0) {
      errorMessage = this.messagesValue.empty_file
    } else if (file.size > this.maxFileSizeValue) {
      errorMessage = interpolateMessage(this.messagesValue.oversized_file, {
        size: formatBytes(this.maxFileSizeValue)
      })
    }
    row.classList.toggle("has-file-error", Boolean(errorMessage))
    error.textContent = errorMessage || ""
    error.hidden = !errorMessage

    const sourceInput = row.querySelector("[data-upload-field='sourcePath']")
    sourceInput.name = `company_user_upload[entries_attributes][${index}][source_path]`
    sourceInput.value = sourcePath
    return row
  }

  renumberRows() {
    this.rowTargets.forEach((row, index) => {
      row.dataset.rowIndex = index
      row.querySelector("[data-upload-field='sourcePath']").name =
        `company_user_upload[entries_attributes][${index}][source_path]`
    })
  }

  syncPickerState(files = this.selectedFiles) {
    const count = this.rowTargets.length
    this.detailsTarget.hidden = count === 0
    this.dropzoneTarget.classList.toggle("has-files", files.length > 0)
    this.pickerTitleTarget.textContent = files.length > 0
      ? this.messagesValue.files_selected
      : this.messagesValue.drop_here
    this.fileCountTarget.textContent = count === 1
      ? this.messagesValue.file_count_one
      : interpolateMessage(this.messagesValue.file_count_other, { count })
    this.totalSizeTarget.textContent = files.length > 0
      ? formatBytes(files.reduce((sum, file) => sum + file.size, 0))
      : this.messagesValue.choose_again_short
  }

  syncFileError(message) {
    this.dropzoneTarget.classList.toggle("error", Boolean(message))
    this.fileErrorTarget.textContent = message || ""
    this.fileErrorTarget.hidden = !message
  }

  get selectedFiles() {
    return this.activeInput ? Array.from(this.activeInput.files) : []
  }
}
